import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Sparkles, CheckCircle, AlertCircle } from 'lucide-react';
import { newsletterAPI } from '../utils/api';
import authService from '../firebase/authService';

const STORAGE_KEY = 'newsletter_popup_dismissed';
const POPUP_DELAY_MS = 10000; // 10 seconds
const DISMISS_DAYS = 30; // Don't show again for 30 days

const NewsletterPopup = () => {
    const [isVisible, setIsVisible] = useState(false);
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState('idle'); // idle, loading, success, error
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        // Check if user is logged in
        const user = authService.getCurrentUser();
        if (user) {
            return; // Don't show popup for logged in users
        }

        // Check if popup was recently dismissed
        const dismissedAt = localStorage.getItem(STORAGE_KEY);
        if (dismissedAt) {
            const dismissDate = new Date(dismissedAt);
            const now = new Date();
            const daysSinceDismiss = (now - dismissDate) / (1000 * 60 * 60 * 24);
            if (daysSinceDismiss < DISMISS_DAYS) {
                return; // Still within dismiss period
            }
        }

        // Show popup after delay
        const timer = setTimeout(() => {
            setIsVisible(true);
        }, POPUP_DELAY_MS);

        // Exit-intent trigger — show when mouse moves toward browser chrome
        let exitIntentFired = false;
        const handleMouseLeave = (e) => {
            if (e.clientY <= 5 && !exitIntentFired) {
                exitIntentFired = true;
                setIsVisible(true);
            }
        };
        document.addEventListener('mouseleave', handleMouseLeave);

        return () => {
            clearTimeout(timer);
            document.removeEventListener('mouseleave', handleMouseLeave);
        };
    }, []);

    const handleDismiss = () => {
        localStorage.setItem(STORAGE_KEY, new Date().toISOString());
        setIsVisible(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!email || !email.includes('@')) {
            setStatus('error');
            setErrorMessage('Please enter a valid email address');
            return;
        }

        setStatus('loading');
        setErrorMessage('');

        try {
            await newsletterAPI.subscribe(email, 'popup');
            setStatus('success');
            // Auto-dismiss after success
            setTimeout(() => {
                localStorage.setItem(STORAGE_KEY, new Date().toISOString());
                setIsVisible(false);
            }, 3000);
        } catch (error) {
            setStatus('error');
            setErrorMessage(error.message || 'Failed to subscribe. Please try again.');
        }
    };

    return (
        <AnimatePresence>
            {isVisible && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={handleDismiss}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
                    />

                    {/* Popup */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none"
                    >
                        <div className="w-full max-w-md bg-[#0F1219] border border-white/10 rounded-3xl p-8 shadow-2xl pointer-events-auto relative overflow-hidden">
                            {/* Background glow */}
                            <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/20 rounded-full blur-[100px] pointer-events-none" />
                            <div className="absolute bottom-0 left-0 w-48 h-48 bg-teal-500/15 rounded-full blur-[80px] pointer-events-none" />

                            {/* Close button */}
                            <button
                                onClick={handleDismiss}
                                className="absolute top-4 right-4 p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>

                            <div className="relative z-10">
                                {status === 'success' ? (
                                    /* Success State */
                                    <div className="text-center py-8">
                                        <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <CheckCircle className="w-8 h-8 text-emerald-400" />
                                        </div>
                                        <h3 className="text-2xl font-bold text-white mb-2">You're In!</h3>
                                        <p className="text-slate-400">
                                            Welcome to our career insights newsletter. Expect great content soon!
                                        </p>
                                    </div>
                                ) : (
                                    /* Form State */
                                    <>
                                        <div className="flex items-center justify-center mb-6">
                                            <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center">
                                                <Sparkles className="w-7 h-7 text-white" />
                                            </div>
                                        </div>

                                        <h3 className="text-2xl font-bold text-white text-center mb-2">
                                            Get Career Insights
                                        </h3>
                                        <p className="text-slate-400 text-center mb-4">
                                            Join 10,000+ professionals getting weekly career tips, salary insights, and job search strategies.
                                        </p>

                                        {/* Benefit bullets */}
                                        <div className="space-y-2 mb-6">
                                            {[
                                                '📈 Weekly salary trends & market data',
                                                '🎯 Curated learning paths & resources',
                                                '💡 Exclusive career growth strategies'
                                            ].map((bullet, i) => (
                                                <div key={i} className="flex items-center space-x-2 text-sm text-slate-300">
                                                    <span>{bullet}</span>
                                                </div>
                                            ))}
                                        </div>

                                        <form onSubmit={handleSubmit} className="space-y-4">
                                            <div className="relative">
                                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                                <input
                                                    type="email"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    placeholder="Enter your email"
                                                    className="w-full pl-12 pr-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-emerald-500/50 focus:ring-2 focus:ring-emerald-500/20 transition-all"
                                                    disabled={status === 'loading'}
                                                />
                                            </div>

                                            {status === 'error' && (
                                                <div className="flex items-center space-x-2 text-red-400 text-sm">
                                                    <AlertCircle className="w-4 h-4" />
                                                    <span>{errorMessage}</span>
                                                </div>
                                            )}

                                            <button
                                                type="submit"
                                                disabled={status === 'loading'}
                                                className="w-full py-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold rounded-xl shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                                            >
                                                {status === 'loading' ? (
                                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                                ) : (
                                                    <>
                                                        <span>Subscribe Free</span>
                                                        <Sparkles className="w-4 h-4" />
                                                    </>
                                                )}
                                            </button>
                                        </form>

                                        <p className="text-xs text-slate-500 text-center mt-4">
                                            No spam, ever. Unsubscribe anytime.
                                        </p>
                                    </>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default NewsletterPopup;
